import { Flex, Form, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { verifyMFALogin } from '@/api/mfa';
import { isAuthErrorResponse } from '@/api/user';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { useApiMutation } from '@/hooks/api';
import { SPACE_MD } from '@/theme/buildAntdTheme';
import FormErrorAlert from '@/UI/FormErrorAlert';
import { SubsectionHeading } from '@/UI/typography';
import { buildMfaFormSchema } from '@/utils/validation/formSchemas/authSchemas';

import type { AuthTokenResponse, MFALoginMethod } from '@/api/user';
import type { SchemaSection } from '@/components/form/SchemaForm/types';

interface MFAStepProps {
  mfaToken: string;
  mfaMethod?: MFALoginMethod;
  /** Shown in the email variant so the user knows which inbox to check. */
  userEmail?: string;
  onSuccess: (loginData: AuthTokenResponse) => void;
  onBack: () => void;
}

const MFAStep = ({
  mfaToken,
  mfaMethod = 'totp',
  userEmail,
  onSuccess,
  onBack,
}: MFAStepProps) => {
  const [form] = Form.useForm();
  const [error, setError] = useState('');
  const [t] = useTranslation();

  const isEmail = mfaMethod === 'email';

  const formSchema = useMemo(() => buildMfaFormSchema(t), [t]);

  const verifyMutation = useApiMutation({
    mutationFn: (otp_token: string) =>
      verifyMFALogin({ mfa_token: mfaToken, otp_token }),
    errorKey: 'MFA_VERIFY_FAILED',
    onSuccess: (result) => {
      if (isAuthErrorResponse(result)) {
        const errorBody = result.data as { detail?: string; message?: string };
        setError(
          errorBody?.detail ?? errorBody?.message ?? t('MFA_INVALID_CODE'),
        );
        return;
      }

      if (result?.access_token) {
        onSuccess(result);
        return;
      }

      setError(t('MFA_VERIFY_FAILED'));
    },
  });

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'mfa',
        fields: [
          {
            name: 'otp_token',
            type: 'text',
            label: isEmail ? t('MFA_METHOD_EMAIL_TITLE') : t('MFA_CODE_LABEL'),
            componentProps: {
              placeholder: '000000',
              maxLength: 20,
              autoFocus: true,
              autoComplete: 'one-time-code',
              size: 'large',
            },
          },
        ],
      },
    ],
    [isEmail, t],
  );

  const onFinish = ({ otp_token }: { otp_token: string }) => {
    setError('');
    verifyMutation.mutate(otp_token);
  };

  return (
    <Flex vertical gap={SPACE_MD}>
      <SubsectionHeading>{t('MFA_SECTION_TITLE')}</SubsectionHeading>
      <Typography.Text type="secondary">
        {isEmail
          ? t('MFA_STEP_EMAIL_DESCRIPTION', {
              email: userEmail || t('MFA_YOUR_EMAIL'),
            })
          : t('MFA_STEP_DESCRIPTION')}
      </Typography.Text>
      <FormErrorAlert
        error={error || verifyMutation.error}
        fallbackKey="MFA_VERIFY_FAILED"
      />
      <SchemaForm
        form={form}
        formSchema={formSchema}
        name="mfa"
        onFinish={(values: { otp_token: string }) => onFinish(values)}
        sections={sections}
        footer={{
          actionSize: 'large',
          actions: [
            {
              label: t('MFA_VERIFY_BUTTON'),
              htmlType: 'submit',
              type: 'primary',
              loading: verifyMutation.loading,
            },
          ],
        }}
      />
      <Typography.Link onClick={onBack}>
        ← {t('MFA_BACK_TO_LOGIN')}
      </Typography.Link>
    </Flex>
  );
};

export default MFAStep;
